import { useCallback } from 'react';
import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { FIVE_MINUTES } from '@constants';
import {
  getIntegrationMetadataApi,
  IntegrationMetadataEntry,
  IntegrationMetadataResponse,
} from 'IntegrationModule/api/getIntegrationMetadataApi';
import { integrationQueryKeys } from './integrationQueryKeys';

const EMPTY_ENTRY: IntegrationMetadataEntry = {
  externalDocsUrl: null,
  internalDocsUrl: null,
  githubRepoUrl: null,
  runbookUrl: null,
  grafanaDashboardUrl: null,
};

/**
 * Per-integration documentation and reference links served by galaxy-server.
 * Replaces the hard-coded sites.google.com / GitHub URLs in the FE.
 *
 * Use the returned `metadataFor(targetService)` helper to read a specific
 * integration's entry — falls back to all-null fields if the service isn't in
 * the config so consumers can render "only the present links" uniformly.
 */
export const useIntegrationMetadataQuery = () => {
  const { data, ...query } = useAppQuery<IntegrationMetadataResponse>({
    queryKey: integrationQueryKeys.list({
      identifier: 'useIntegrationMetadataQuery',
    }),
    queryFn: async ({ token }) => {
      const response = await getIntegrationMetadataApi({
        params: {},
        token,
      });
      return (response.data ?? {}) as IntegrationMetadataResponse;
    },
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
  });

  const metadata = data ?? {};

  // Stable identity across renders so consumers can include `metadataFor` in
  // their useMemo deps without thrashing.
  const metadataFor = useCallback(
    (targetService: string): IntegrationMetadataEntry => metadata[targetService] ?? EMPTY_ENTRY,
    [metadata],
  );

  return {
    metadata,
    metadataFor,
    ...query,
  };
};
